import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getSystemHealth, getBusinessDashboard } from "@/lib/health.functions";
import * as flags from "@/lib/feature-flags";

export const Route = createFileRoute("/_authenticated/health")({
  component: HealthPage,
  head: () => ({ meta: [{ title: "System Health · Smartasaurus Admin" }] }),
});

const dot = (s: "ok" | "warn" | "fail") =>
  s === "ok" ? "🟢" : s === "warn" ? "🟡" : "🔴";

function HealthPage() {
  const healthFn = useServerFn(getSystemHealth);
  const dashFn = useServerFn(getBusinessDashboard);
  const health = useQuery({ queryKey: ["sys-health"], queryFn: () => healthFn(), refetchInterval: 60_000 });
  const dash = useQuery({ queryKey: ["biz-dash"], queryFn: () => dashFn(), refetchInterval: 60_000 });

  const flagEntries = Object.entries(flags).filter(([k, v]) =>
    typeof v === "boolean" && k === k.toUpperCase(),
  ) as [string, boolean][];

  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl uppercase">System Health</h1>
          <p className="text-sm text-muted-foreground">Admin overview · auto-refreshes every 60s</p>
        </div>
        <Link to="/me" className="text-sm underline">Back</Link>
      </header>

      {/* Health checks */}
      <section className="mb-10">
        <h2 className="mb-3 text-sm uppercase tracking-wider text-muted-foreground">Health Checks</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {(health.data?.checks ?? []).map((c) => (
            <div key={c.key} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">{c.label}</span>
                <span>{dot(c.status)}</span>
              </div>
              {c.detail && <p className="mt-1 text-xs text-muted-foreground">{c.detail}</p>}
            </div>
          ))}
          {health.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        </div>
      </section>

      {/* Business KPIs */}
      <section className="mb-10">
        <h2 className="mb-3 text-sm uppercase tracking-wider text-muted-foreground">Business Dashboard</h2>
        {dash.data ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Kpi label="Visitors 24h" value={dash.data.visitors24h} />
            <Kpi label="Visitors 7d" value={dash.data.visitors7d} />
            <Kpi label="Newsletter" value={dash.data.newsletterSubs} />
            <Kpi label="Discovery views 7d" value={dash.data.discoveryViews7d} />
            <Kpi label="Quiz plays 7d" value={dash.data.quizPlays7d} />
            <Kpi label="Orders" value={dash.data.ordersTotal} />
            <Kpi label="Revenue" value={`€${(dash.data.revenueCents / 100).toFixed(2)}`} />
            <Kpi label="Profiles" value={dash.data.profiles} />
            <Kpi label="Discoveries" value={dash.data.discoveries} />
            <Kpi label="Collections" value={dash.data.collections} />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
        {dash.data?.topDiscovery && (
          <p className="mt-4 text-sm">
            <span className="text-muted-foreground">Top discovery (7d): </span>
            <span className="font-medium">{dash.data.topDiscovery.title}</span>
            <span className="text-muted-foreground"> · {dash.data.topDiscovery.count} views</span>
          </p>
        )}
      </section>

      {/* Feature flags */}
      <section>
        <h2 className="mb-3 text-sm uppercase tracking-wider text-muted-foreground">Feature Flags</h2>
        <div className="grid gap-2 sm:grid-cols-2">
          {flagEntries.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-sm">
              <span className="font-mono text-xs">{k}</span>
              <span className={v ? "text-emerald-400" : "text-muted-foreground"}>{v ? "ON" : "OFF"}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Edit <code className="font-mono">src/lib/feature-flags.ts</code> to flip. Commerce stays dormant until commerce flags flip together with <code>PUBLIC_BETA_MODE</code>.
        </p>
      </section>
    </main>
  );
}

function Kpi({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl">{value}</div>
    </div>
  );
}
